import {
	type RefObject,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react'

/** Breathing gap (px) between the scrolled-up search card and the sticky chrome. */
const SEARCH_SCROLL_GAP = 4

/** Nearest scrollable ancestor of `node`, falling back to the document scroller. */
function getScrollParent(node: HTMLElement | null): HTMLElement {
	let el = node?.parentElement ?? null
	while (el) {
		const { overflowY } = getComputedStyle(el)
		if (
			(overflowY === 'auto' || overflowY === 'scroll') &&
			el.scrollHeight > el.clientHeight
		) {
			return el
		}
		el = el.parentElement
	}
	const scrolling = document.scrollingElement
	return scrolling instanceof HTMLElement
		? scrolling
		: document.documentElement
}

interface UseMobileSearchAnchorArgs {
	/** The mobile search is open (desktop keeps its own always-visible field). */
	active: boolean
	/** Sticky tab bar, used to clear the card of the sticky chrome on open. */
	stickyHeaderRef: RefObject<HTMLElement | null>
	/** The rendered list; its identity change re-runs the measurement. */
	measureKey: unknown
}

/**
 * Keeps the page from jumping while a mobile search filters a list. On open it
 * scrolls the card to the top (below the sticky chrome) and records the document
 * height that fills the viewport from there; while typing it pads the page
 * bottom only down to that target, so the scroll position holds with the least
 * empty space. Shared by the check-in and attendees tables.
 *
 * Wire `cardRef` to the list card, `spacerRef` + `bottomSpacer` to a trailing
 * spacer div, and `onSearchFocus` / `onSearchBlur` to the search field.
 */
export function useMobileSearchAnchor({
	active,
	stickyHeaderRef,
	measureKey
}: UseMobileSearchAnchorArgs) {
	const cardRef = useRef<HTMLDivElement>(null)
	const spacerRef = useRef<HTMLDivElement>(null)
	const targetDocHeight = useRef(0)
	const [bottomSpacer, setBottomSpacer] = useState(0)
	// The spacer only earns its keep while the field is focused (i.e. actively
	// typing). Once the input blurs, the reserved space is just dead whitespace.
	const [isFocused, setIsFocused] = useState(false)

	// On open, bring the search to the top, resting just below the sticky app
	// header + tab bar. scrollIntoView finds the real scroll container (the body
	// isn't the scroller here); scroll-margin keeps the card clear of the sticky
	// chrome, sized live from the tab bar's own offset + height, so there's no
	// hardcoded value to drift from the layout.
	useEffect(() => {
		if (!active) {
			return
		}
		const card = cardRef.current
		if (!card) {
			return
		}
		const tabs = stickyHeaderRef.current
		if (tabs) {
			const stickyTop = Number.parseFloat(getComputedStyle(tabs).top) || 0
			card.style.scrollMarginTop = `${stickyTop + tabs.offsetHeight + SEARCH_SCROLL_GAP}px`
		}
		card.scrollIntoView({ block: 'start', behavior: 'instant' })
		// With the card pinned, this is the shortest the document can be while still
		// filling the viewport — the height the bottom spacer holds during filtering.
		const scroller = getScrollParent(card)
		targetDocHeight.current = scroller.scrollTop + scroller.clientHeight
	}, [active, stickyHeaderRef])

	// Pad the page bottom just enough to keep the document at its target height, so
	// filtering the list can't shrink it under the current scroll (which is what
	// makes the page jump). Reserves only the deficit, never the whole list.
	//
	// `measureKey` (the filtered list) is a dependency on purpose even though the
	// body never reads it: the measurement is taken from the DOM (`scrollHeight`),
	// so the effect has to re-run *after* the filtered list re-paints to a new
	// height. Its identity change is exactly that "the list changed, re-measure"
	// signal — hence the biome-ignore for the otherwise-unused dep.
	// biome-ignore lint/correctness/useExhaustiveDependencies: measureKey is a re-measure trigger read from the DOM, not from the value — see comment above
	useLayoutEffect(() => {
		if (!(active && isFocused)) {
			setBottomSpacer(0)
			return
		}
		const scroller = getScrollParent(cardRef.current)
		const naturalHeight =
			scroller.scrollHeight - (spacerRef.current?.offsetHeight ?? 0)
		setBottomSpacer(Math.max(0, targetDocHeight.current - naturalHeight))
	}, [active, isFocused, measureKey])

	return {
		cardRef,
		spacerRef,
		bottomSpacer,
		onSearchFocus: () => setIsFocused(true),
		onSearchBlur: () => setIsFocused(false)
	}
}
